import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '../../../config/api';

const MatchApiSettings = () => {
  const [settings, setSettings] = useState({
    sofaScoreEnabled: false,
    manualMatchesEnabled: true,
    sofaScoreSports: ['football']
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [testResult, setTestResult] = useState(null);
  const [testing, setTesting] = useState(false);

  const availableSports = [
    { value: 'football', label: 'Football/Soccer', emoji: '⚽' },
    { value: 'cricket', label: 'Cricket', emoji: '🏏' },
    { value: 'tennis', label: 'Tennis', emoji: '🎾' },
    { value: 'basketball', label: 'Basketball', emoji: '🏀' },
    { value: 'badminton', label: 'Badminton', emoji: '🏸' },
    { value: 'volleyball', label: 'Volleyball', emoji: '🏐' },
    { value: 'hockey', label: 'Hockey', emoji: '🏒' },
    { value: 'rugby', label: 'Rugby', emoji: '🏉' },
    { value: 'baseball', label: 'Baseball', emoji: '⚾' },
    { value: 'american-football', label: 'American Football', emoji: '🏈' },
    { value: 'motorsport', label: 'Motorsport', emoji: '🏎️' },
    { value: 'esports', label: 'Esports', emoji: '🎮' }
  ];

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_BASE_URL}/api/admin/match-api-settings`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setSettings({
          sofaScoreEnabled: data.sofaScoreEnabled || false,
          manualMatchesEnabled: data.manualMatchesEnabled !== false,
          sofaScoreSports: data.sofaScoreSports || ['football']
        });
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
      setMessage('Error loading settings');
    }
    setLoading(false);
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage('');

    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_BASE_URL}/api/admin/match-api-settings`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(settings)
      });

      if (response.ok) {
        setMessage('Settings saved successfully!');
        setTimeout(() => setMessage(''), 3000);
      } else {
        const error = await response.json();
        setMessage(error.message || 'Error saving settings');
      }
    } catch (error) {
      console.error('Error saving settings:', error);
      setMessage('Network error. Please try again.');
    }
    setSaving(false);
  };

  const handleSportToggle = (sport) => {
    setSettings(prev => {
      const newSports = prev.sofaScoreSports.includes(sport)
        ? prev.sofaScoreSports.filter(s => s !== sport)
        : [...prev.sofaScoreSports, sport];
      return { ...prev, sofaScoreSports: newSports };
    });
  };

  const testSofaScoreApi = async () => {
    setTesting(true);
    setTestResult(null);

    try {
      // Get today's date
      const today = new Date().toISOString().split('T')[0];
      const sport = settings.sofaScoreSports[0] || 'football';

      const response = await fetch(`${API_BASE_URL}/api/sofascore/events/${today}?sport=${sport}`);
      const data = await response.json();

      if (data.events && data.events.length > 0) {
        setTestResult({
          success: true,
          message: `Found ${data.events.length} ${sport} events for today`,
          sampleEvents: data.events.slice(0, 3)
        });
      } else if (data.message) {
        setTestResult({
          success: false,
          message: data.message
        });
      } else {
        setTestResult({
          success: true,
          message: `No events found for ${sport} today, but API is working`
        });
      }
    } catch (error) {
      setTestResult({
        success: false,
        message: 'Failed to connect to SofaScore API'
      });
    }
    setTesting(false);
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow p-6">
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-xl shadow p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Match API Settings</h2>
            <p className="text-gray-600 mt-1">Configure external match data sources</p>
          </div>
          <button
            onClick={handleSave}
            disabled={saving}
            className={`px-6 py-2 rounded-lg font-medium transition-colors ${
              saving
                ? 'bg-gray-300 cursor-not-allowed'
                : 'bg-blue-600 hover:bg-blue-700 text-white'
            }`}
          >
            {saving ? 'Saving...' : 'Save Settings'}
          </button>
        </div>

        {message && (
          <div className={`p-4 rounded-lg mb-6 ${
            message.includes('success') || message.includes('Success')
              ? 'bg-green-100 text-green-800'
              : 'bg-red-100 text-red-800'
          }`}>
            {message}
          </div>
        )}

        {/* Main Toggle Settings */}
        <div className="grid md:grid-cols-2 gap-6">
          {/* SofaScore API Toggle */}
          <div className="border rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center space-x-3">
                <span className="text-2xl">🌐</span>
                <div>
                  <h3 className="font-semibold text-gray-900">SofaScore API</h3>
                  <p className="text-sm text-gray-500">Fetch matches from SofaScore</p>
                </div>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={settings.sofaScoreEnabled}
                  onChange={(e) => setSettings({ ...settings, sofaScoreEnabled: e.target.checked })}
                  className="sr-only peer"
                />
                <div className="w-14 h-7 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[4px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-blue-600"></div>
              </label>
            </div>
            <div className={`text-xs ${settings.sofaScoreEnabled ? 'text-green-600' : 'text-gray-400'}`}>
              {settings.sofaScoreEnabled ? '✓ API is enabled' : 'API is disabled'}
            </div>
          </div>

          {/* Manual Matches Toggle */}
          <div className="border rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center space-x-3">
                <span className="text-2xl">✏️</span>
                <div>
                  <h3 className="font-semibold text-gray-900">Manual Matches</h3>
                  <p className="text-sm text-gray-500">Show manually added matches</p>
                </div>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={settings.manualMatchesEnabled}
                  onChange={(e) => setSettings({ ...settings, manualMatchesEnabled: e.target.checked })}
                  className="sr-only peer"
                />
                <div className="w-14 h-7 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[4px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-blue-600"></div>
              </label>
            </div>
            <div className={`text-xs ${settings.manualMatchesEnabled ? 'text-green-600' : 'text-gray-400'}`}>
              {settings.manualMatchesEnabled ? '✓ Manual matches are shown' : 'Manual matches are hidden'}
            </div>
          </div>
        </div>
      </div>

      {/* Sport Selection */}
      <div className="bg-white rounded-xl shadow p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">SofaScore Sports Selection</h3>
        <p className="text-gray-600 text-sm mb-4">
          Select which sports to fetch from SofaScore API. Only enabled sports will show matches from the API.
        </p>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          {availableSports.map((sport) => (
            <button
              key={sport.value}
              onClick={() => handleSportToggle(sport.value)}
              disabled={!settings.sofaScoreEnabled}
              className={`flex items-center space-x-2 p-3 rounded-lg border transition-all ${
                !settings.sofaScoreEnabled
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : settings.sofaScoreSports.includes(sport.value)
                  ? 'bg-blue-50 border-blue-500 text-blue-700'
                  : 'bg-white border-gray-200 hover:border-gray-300 text-gray-700'
              }`}
            >
              <span className="text-xl">{sport.emoji}</span>
              <span className="text-sm font-medium">{sport.label}</span>
              {settings.sofaScoreSports.includes(sport.value) && settings.sofaScoreEnabled && (
                <span className="ml-auto text-blue-500">✓</span>
              )}
            </button>
          ))}
        </div>

        {settings.sofaScoreEnabled && settings.sofaScoreSports.length === 0 && (
          <p className="text-amber-600 text-sm mt-3">
            ⚠️ Please select at least one sport to fetch from SofaScore
          </p>
        )}
      </div>

      {/* API Test Section */}
      <div className="bg-white rounded-xl shadow p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Test API Connection</h3>
        <p className="text-gray-600 text-sm mb-4">
          Test the SofaScore API connection to verify it's working correctly.
        </p>

        <button
          onClick={testSofaScoreApi}
          disabled={testing || !settings.sofaScoreEnabled}
          className={`px-4 py-2 rounded-lg font-medium transition-colors ${
            testing || !settings.sofaScoreEnabled
              ? 'bg-gray-300 cursor-not-allowed text-gray-500'
              : 'bg-green-600 hover:bg-green-700 text-white'
          }`}
        >
          {testing ? 'Testing...' : 'Test SofaScore API'}
        </button>

        {!settings.sofaScoreEnabled && (
          <p className="text-sm text-gray-500 mt-2">Enable SofaScore API to test the connection</p>
        )}

        {testResult && (
          <div className={`mt-4 p-4 rounded-lg ${
            testResult.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'
          }`}>
            <div className={`font-medium ${testResult.success ? 'text-green-800' : 'text-red-800'}`}>
              {testResult.success ? '✓ ' : '✗ '}{testResult.message}
            </div>
            
            {testResult.sampleEvents && testResult.sampleEvents.length > 0 && (
              <div className="mt-3">
                <p className="text-sm text-gray-600 mb-2">Sample events:</p>
                <div className="space-y-2">
                  {testResult.sampleEvents.map((event, index) => (
                    <div key={index} className="text-sm bg-white p-2 rounded border">
                      <span className="font-medium">{event.homeTeam?.name || 'Home'}</span>
                      <span className="text-gray-400 mx-2">vs</span>
                      <span className="font-medium">{event.awayTeam?.name || 'Away'}</span>
                      <span className="text-gray-500 ml-2 text-xs">
                        ({event.tournament?.name || 'Tournament'})
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Info Section */}
      <div className="bg-blue-50 rounded-xl p-6">
        <h3 className="text-lg font-semibold text-blue-900 mb-2">ℹ️ How it works</h3>
        <ul className="text-sm text-blue-800 space-y-2">
          <li>• <strong>SofaScore API:</strong> When enabled, matches from SofaScore will appear alongside manually added matches in the Match Vote page.</li>
          <li>• <strong>Manual Matches:</strong> You can still add matches manually through the Match Management page. These will always be fully functional with voting and comments.</li>
          <li>• <strong>Combined View:</strong> Both sources are merged and sorted by start time in the Match Vote page.</li>
          <li>• <strong>SofaScore Matches:</strong> Matches from SofaScore show real-time scores but voting/comments work differently (stored locally).</li>
        </ul>
      </div>
    </div>
  );
};

export default MatchApiSettings;
